import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector } from '../../../../app/hooks';

import { useGetCustomersQuery } from '../../../../services/entities/impl/blfApi/endpoints/customers';
import {
  useAddRoleMutation,
  useDeleteRoleMutation,
} from '../../../../services/entities/impl/blfApi/endpoints/permissions';
import {
  useGetUserMeQuery,
  useGetUsersQuery,
} from '../../../../services/entities/impl/blfApi/endpoints/users';
import { Customer } from '../../../../services/entities/impl/blfApi/schemas/customer';
import {
  Role,
  User,
  UserRolesEnum,
} from '../../../../services/entities/impl/blfApi/schemas/user';
import { selectCurrOnboardUserID } from '../../onboardingSlice';
import { GetEntityResponse } from '../generic/context/CRUDContext';

export enum RolesFormState {
  Idle = 'Idle',
  Saving = 'Saving',
  Saved = 'Saved',
  Error = 'Error',
}

const isValidRole = (role: Role) => {
  if (!role.name || !role.resourceID) {
    return false;
  }
  return true;
};

const roleToAvailableRolesMap: {
  [key in UserRolesEnum]: UserRolesEnum[];
} = {
  [UserRolesEnum.root]: Object.values(UserRolesEnum),
  [UserRolesEnum.admin]: [UserRolesEnum.admin, UserRolesEnum.viewer],
  [UserRolesEnum.qa]: [],
  [UserRolesEnum.viewer]: [],
};

const getAuthorizedUserAllowedRolesAndCustomers = ({
  customers,
  currUser,
}: {
  customers?: Customer[];
  currUser?: User;
}) => {
  if (!customers || !currUser)
    return {
      allowedRolesOptions: [],
      allowedCustomersIds: [],
    };

  const allowedRolesOptions = new Set<UserRolesEnum>();
  const allowedCustomersIds = new Set<string>();

  currUser.roles.forEach((role) => {
    const allowedRoles = roleToAvailableRolesMap[role.name];
    allowedRoles.forEach((allowedRole) => allowedRolesOptions.add(allowedRole));
  });

  customers.forEach((customer) => {
    if (allowedRolesOptions.has(UserRolesEnum.root)) {
      allowedCustomersIds.add(customer.id);
      return;
    }

    if (customer.id === currUser.customerID) {
      allowedCustomersIds.add(customer.id);
    }

    currUser.roles.some((role) => {
      if (
        role.resourceID === customer.id &&
        role.name === UserRolesEnum.admin
      ) {
        allowedCustomersIds.add(customer.id);
      }
    });
  });

  return {
    allowedRolesOptions: Array.from(allowedRolesOptions),
    allowedCustomersIds: Array.from(allowedCustomersIds),
  };
};

export const useRolesFormData = ({
  useGetCurrOnboardUserHook,
}: {
  useGetCurrOnboardUserHook: () => GetEntityResponse<User>;
}) => {
  const currOnboardUserID = useAppSelector(selectCurrOnboardUserID);
  const [deleteRole] = useDeleteRoleMutation();
  const [addRole] = useAddRoleMutation();
  const { currentData: customers } = useGetCustomersQuery(undefined, {});
  const { currentData: authorizedUser } = useGetUserMeQuery();
  // use status because it's synced between different components
  const { status } = useGetUsersQuery();
  const { currentData: currUser } = useGetCurrOnboardUserHook();

  const { allowedRolesOptions, allowedCustomersIds } =
    getAuthorizedUserAllowedRolesAndCustomers({
      customers,
      currUser: authorizedUser,
    });

  const [roles, setRoles] = useState<(Role & { selector: number | string })[]>(
    []
  );
  const [submitState, setSubmitState] = useState<RolesFormState>(
    RolesFormState.Idle
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (
      !customers ||
      !currUser ||
      !currUser.roles.length ||
      !currOnboardUserID
    ) {
      setRoles([]);
      return;
    }
    const currRoles = currUser.roles.map((role) => ({
      ...role,
      selector: role.id as number | string,
    }));

    setRoles(currRoles);
  }, [currUser, currOnboardUserID]);

  const addCustomer = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const id = uuidv4();
    if (!currUser) return;
    setRoles((prevRoles) => [
      ...prevRoles,
      {
        selector: id,
        name: UserRolesEnum.viewer,
        resourceID: '',
        userID: currUser.id,
      },
    ]);
  };

  const handleCustomerChange = (
    selector: string | number,
    customerID: string
  ) => {
    setRoles((prevRoles) => {
      console.log(prevRoles, selector, customerID);
      return prevRoles.map((role) => {
        if (role.selector === selector) {
          return {
            ...role,
            resourceID: customerID,
          };
        }
        return role;
      });
    });
  };

  const handleRoleChange = (
    selector: string | number,
    roleName: UserRolesEnum
  ) => {
    setRoles((prevRoles) => {
      return prevRoles.map((role) => {
        if (role.selector === selector) {
          return {
            ...role,
            name: roleName,
          };
        }
        return role;
      });
    });
  };

  const handleDeleteRole = async (selector: string | number) => {
    const currRoles = currUser?.roles || [];
    if (currRoles.find((role) => role.id === selector)) {
      try {
        await deleteRole(selector as number).unwrap();
        console.log('Successfully deleted roleID: ', selector);
      } catch (e) {
        console.log(e);
      }
    }
    setRoles((prevRoles) =>
      prevRoles.filter((role) => role.selector !== selector)
    );
  };

  const handleSubmitRoles = useCallback(async () => {
    const userID = currUser?.id;
    const currRoles = currUser?.roles || [];

    try {
      if (roles.length > 0 && userID) {
        // validate roles before sending
        if (roles.map((r) => isValidRole(r)).includes(false)) {
          throw new Error('Role cannot have empty fields');
        }
        // cleanup updated roles
        setSubmitState(RolesFormState.Saving);
        const rolesToDelete = currRoles
          .filter(
            (role) =>
              !roles.find(
                (r) => r.name === role.name && r.resourceID === role.resourceID
              )
          )
          .map((role) => role.id);

        if (rolesToDelete.length > 0) {
          rolesToDelete.forEach(async (roleID) => {
            try {
              await deleteRole(roleID as number).unwrap();
              console.log('Successfully deleted roleID: ', roleID);
            } catch (e) {
              throw new Error(`Error deleting role with id ${roleID}`);
            }
          });
        }
        // iterate through roles defined for customers
        roles.forEach(async (role) => {
          // if this role already exists - skip.
          if (
            currRoles.find(
              (r) => r.name === role.name && r.resourceID === role.resourceID
            ) ||
            role.resourceID === ''
          )
            return;

          try {
            const res = await addRole(role).unwrap();
            console.log('Successfully added role: ', res);
          } catch (e) {
            throw new Error(
              `Error adding role ${role.name} to user ${role.userID} for customer ${role.resourceID}`
            );
          }
        });
        setSubmitState(RolesFormState.Saved);
      }
    } catch (e) {
      console.error(e);
      setError((e as Error).message);
      setSubmitState(RolesFormState.Error);
    } finally {
      setTimeout(() => {
        setSubmitState(RolesFormState.Idle);
        setError(null);
      }, 5000);
    }
  }, [roles, currUser]);

  return {
    allowedRolesOptions,
    currUser,
    roles,
    submitState:
      status === QueryStatus.pending ? RolesFormState.Saving : submitState,
    error,
    customers: customers?.filter((customer) =>
      allowedCustomersIds.includes(customer.id)
    ),
    addCustomer,
    handleCustomerChange,
    handleRoleChange,
    handleDeleteRole,
    handleSubmitRoles,
  };
};
